import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { numberAsCurrency, copyToClipboard } from '../../utils/index';
import moment from 'moment';
import { toast } from 'react-toastify';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import TouchAppIcon from '@material-ui/icons/TouchApp';

const PAYMENT_WINDOW_MS = 20 * 60 * 1000;

const useStyles = makeStyles((theme) => ({
  wrap: {
    marginBottom: '16px',
  },
  timerSection: {
    backgroundColor: '#E8F0FE',
    borderRadius: '12px',
    padding: '12px 14px',
    marginBottom: '12px',
    textAlign: 'left',
  },
  timerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    color: '#1A1A1A',
    fontSize: '0.875rem',
    '& strong': {
      fontWeight: 700,
    },
  },
  timerIcon: {
    color: '#2E7D32',
    fontSize: '1.35rem',
    flexShrink: 0,
  },
  progressTrack: {
    height: '8px',
    borderRadius: '999px',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '999px',
    background: 'linear-gradient(90deg, #56F84B 0%, #2E7D32 100%)',
    transition: 'width 0.3s linear',
  },
  paymentBox: {
    backgroundColor: '#214722',
    borderRadius: '14px',
    padding: '18px 16px',
    color: '#FFFFFF',
    textAlign: 'center',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
  },
  instruction: {
    fontSize: '0.875rem',
    lineHeight: 1.45,
    marginBottom: '14px',
    color: 'rgba(255, 255, 255, 0.95)',
    textAlign: 'center',
  },
  qrToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 14px',
    marginBottom: '14px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#153516',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: '0.9rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#1E491F',
    },
  },
  qrToggleIcon: {
    transition: 'transform 0.2s',
    color: '#FFFFFF !important',
    fill: '#FFFFFF !important',
  },
  qrToggleIconOpen: {
    transform: 'rotate(180deg)',
  },
  qrImageWrap: {
    textAlign: 'center',
    marginBottom: '16px',
    '& img': {
      maxWidth: '220px',
      width: '100%',
      height: 'auto',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      padding: '8px',
    },
  },
  priceBlock: {
    textAlign: 'center',
    marginBottom: '16px',
  },
  priceLarge: {
    fontSize: '1.75rem',
    fontWeight: 800,
    color: '#FFFFFF',
    letterSpacing: '-0.02em',
  },
  priceSub: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.85)',
    marginTop: '4px',
  },
  copyRow: {
    display: 'flex',
    gap: 0,
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '14px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  copyInput: {
    flex: 1,
    minWidth: 0,
    padding: '12px 10px',
    border: 'none',
    backgroundColor: '#153516',
    color: '#FFFFFF',
    fontSize: '0.72rem',
    outline: 'none',
  },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 14px',
    border: 'none',
    backgroundColor: '#90EE90',
    color: '#1A1A1A',
    fontWeight: 700,
    fontSize: '0.8rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#7FE07F',
    },
  },
  copyBtnIcon: {
    fontSize: '1rem',
  },
  warning: {
    fontSize: '0.72rem',
    lineHeight: 1.45,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '16px',
  },
  paidBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#90EE90',
    color: '#1A1A1A',
    fontWeight: 800,
    fontSize: '1rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#7FE07F',
    },
    '&:disabled': {
      backgroundColor: '#b0e0b0',
      cursor: 'not-allowed',
      opacity: 0.8,
    },
  },
  paidBtnIcon: {
    fontSize: '1.35rem',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  spinningIcon: {
    animation: '$spin 1s linear infinite',
  },
}));

export default function OrderQRCode({ order, onPaidClick }) {
  const classes = useStyles();
  const [showQr, setShowQr] = useState(() => window.innerWidth >= 600);
  const [qrCodeDataUrl, setQRCodeDataUrl] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [paidLoading, setPaidLoading] = useState(false);

  const visible =
    order &&
    order.status === 'waiting_payment' &&
    !moment(order.created_at).isBefore(moment().subtract(20, 'minutes'));

  useEffect(() => {
    setPaidLoading(false);
  }, [order]);

  useEffect(() => {
    if (!visible || !order?.qr_code_text) return;
    QRCode.toDataURL(order.qr_code_text, (err, url) => {
      if (!err) setQRCodeDataUrl(url);
    });
  }, [order, visible]);

  useEffect(() => {
    if (!visible || !order?.created_at) return;
    const createdTime = new Date(order.created_at).getTime();
    const endTime = createdTime + PAYMENT_WINDOW_MS;
    const tick = () => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        setTimeRemaining(0);
        window.location.reload();
        return;
      }
      setTimeRemaining(remaining);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [order, visible]);

  function clipboardQRCode() {
    try {
      copyToClipboard(order.qr_code_text);
      toast.success('Código copiado');
    } catch (err) {
      toast.error('Erro ao copiar código. Selecione o código e copie manualmente.');
    }
  }

  function selectText(event) {
    event.target.focus();
    event.target.setSelectionRange(0, 9999);
    event.preventDefault();
  }

  if (!visible) return null;

  const minutesRemaining = Math.floor(timeRemaining / (60 * 1000));
  const secondsRemaining = Math.floor((timeRemaining % (60 * 1000)) / 1000);
  const progressPct =
    PAYMENT_WINDOW_MS > 0
      ? Math.min(100, Math.max(0, (timeRemaining / PAYMENT_WINDOW_MS) * 100))
      : 0;

  return (
    <div className={classes.wrap}>
      <div className={classes.timerSection}>
        <div className={classes.timerRow}>
          <AutorenewIcon className={classes.timerIcon} />
          <span>
            Tempo restante para o pagamento:{' '}
            <strong>
              {minutesRemaining}m {secondsRemaining}s
            </strong>
          </span>
        </div>
        <div className={classes.progressTrack}>
          <div className={classes.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className={classes.paymentBox}>
        <Typography component="p" className={classes.instruction}>
          Copie o código &quot;copia e cola&quot; abaixo ou abra o QR Code para pagar.
        </Typography>

        <button
          type="button"
          className={classes.qrToggle}
          onClick={() => setShowQr((v) => !v)}
        >
          <span>{showQr ? 'Ocultar QR Code' : 'Exibir QR Code'}</span>
          <ExpandMoreIcon
            className={`${classes.qrToggleIcon} ${showQr ? classes.qrToggleIconOpen : ''}`}
          />
        </button>

        {showQr && qrCodeDataUrl && (
          <div className={classes.qrImageWrap}>
            <img src={qrCodeDataUrl} alt="QR Code PIX" />
          </div>
        )}

        <div className={classes.priceBlock}>
          <div className={classes.priceLarge}>{numberAsCurrency(order.total_value)}</div>
          <div className={classes.priceSub}>
            Valor a pagar | {order.numbers_count} títulos
          </div>
        </div>

        <div className={classes.copyRow}>
          <input
            type="text"
            readOnly
            className={classes.copyInput}
            value={order.qr_code_text || ''}
            onClick={selectText}
          />
          <button type="button" className={classes.copyBtn} onClick={clipboardQRCode}>
            <TouchAppIcon className={classes.copyBtnIcon} />
            Copiar código
          </button>
        </div>

        <Typography component="p" className={classes.warning}>
          Se o pagamento não for confirmado no tempo, os números voltam a ficar disponíveis para outros
          participantes.
        </Typography>

        <button
          type="button"
          className={classes.paidBtn}
          disabled={paidLoading}
          onClick={() => {
            if (paidLoading) return;
            setPaidLoading(true);
            onPaidClick && onPaidClick();
          }}
        >
          {paidLoading ? (
            <>
              <AutorenewIcon className={`${classes.paidBtnIcon} ${classes.spinningIcon}`} />
              Verificando pagamento...
            </>
          ) : (
            <>
              <CheckIcon className={classes.paidBtnIcon} />
              Já fiz o pagamento
            </>
          )}
        </button>
      </div>
    </div>
  );
}
